import {Component, Host, h, Prop, Event, EventEmitter, Listen, Element, State, Method} from '@stencil/core';
import moment, {Moment} from "moment";
import {DateFormat, NestDatePickerChangeView, NestDatePickerView} from "./nest-date-picker.interfaces";

/**
 * A simple component for formatting names
 *
 * This component will do some neat things!
 */
@Component({
  tag: 'nest-date-picker',
  styleUrl: 'nest-date-picker.scss',
  shadow: true,
})
export class NestDatePicker {
  nativeInput?: HTMLInputElement;

  @Element() el: HTMLElement;

  /* State */
  @State() selectedDate: Moment;

  @State() activeDate: Moment;

  @State() viewType: NestDatePickerView = 'decade';

  /*-- Props */
  @Prop()
  mode: 'single' | 'range' = 'single';

  @Prop({ mutable: true })
  value: string | Moment | Date;

  @Prop()
  min: string | Date | Moment;

  @Prop()
  max: string | Date | Moment;

  @Prop({ reflect: true })
  clearable: boolean = true;

  @Prop()
  label: string | null;

  @Prop()
  placeholder: string = DateFormat;

  @Prop({ mutable: true })
  showCalendar: boolean = false;

  /*--- Events */
  @Event() nestChange!: EventEmitter<any | null>;

  @Event() nestOpen: EventEmitter<void>;

  @Event() nestHide: EventEmitter<void>;


  toggle = (): void => {
    !this.showCalendar ? this.show() : this.hide();
  }

  onInput = (event: InputEvent) => {
    const inputValue: string = this.getInputTarget(event).value;
    const dateValue: Moment = this.parseDate(inputValue);

    if(!dateValue)
      this.value = dateValue;
  }

  private getInputTarget(ev: Event): HTMLInputElement | null {
    return ev.target as HTMLInputElement | null
  }

  clearInput() {
    void this.hide();

    this.selectedDate = null;
    this.value = this.selectedDate;

    this.viewType = 'day';
    this.nestChange.emit(this.selectedDate)
  }

  @Listen('click', {target: 'window'})
  clickOutside(event): void {
    if (!this.el.contains(event.target)) {
      void this.hide();
    }
  }

  @Listen('changeView')
  changeViewHandler(event: CustomEvent<NestDatePickerChangeView>) {
    const {view, answer} = event.detail;

    if(answer) this.activeDate = moment(answer);
    if(view) this.viewType = view;
  }


  @Listen('selectAnswer')
  selectAnswer({detail}: CustomEvent<Moment>) {
    this.selectedDate = moment(detail);
    this.value = this.selectedDate.toDate();
    this.nestChange.emit(this.value);

    void this.hide();
  }

  @Method() async show(): Promise<void> {
    this.showCalendar = true
    this.viewType = this.selectedDate?.isValid() ? 'day' : 'decade'

    this.nestOpen.emit()
  }

  @Method() async hide(): Promise<void> {
    if (!this.showCalendar) return;
    this.showCalendar = false;
    this.nestHide.emit()
  }

  parseDate(date: string | Moment | Date): Moment | null {
    if(typeof date === 'string') {
      date = moment(date, DateFormat);
    }

    let result: Moment = moment(date);
    return result.isValid() ? result : null;
  }

  componentWillLoad() {
    this.selectedDate = this.parseDate(this.value);
    this.activeDate = this.selectedDate || this.activeDate || moment();
  }

  render() {
    const value: string = this.selectedDate ? this.selectedDate.format(DateFormat) : null;

    const minDate: Moment = this.parseDate(this.min);
    const maxDate: Moment = this.parseDate(this.max);

    let contentView = '';

    switch(this.viewType) {
      case "decade":
        contentView = <nest-date-picker-decade
          activeDate={this.activeDate}
          selectedDate={this.selectedDate}
          minDate={minDate}
          maxDate={maxDate}
        />;
        break;
      case "year":
        contentView = <nest-date-picker-year
          activeDate={this.activeDate}
          selectedDate={this.selectedDate}
          minDate={minDate}
          maxDate={maxDate}
        />;
        break;
      case "month":
        contentView = <nest-date-picker-month
          activeDate={this.activeDate}
          selectedDate={this.selectedDate}
          minDate={minDate}
          maxDate={maxDate}
        />;
        break;
      default:
        contentView = <nest-date-picker-day
          activeDate={this.activeDate}
          selectedDate={this.selectedDate}
          minDate={minDate}
          maxDate={maxDate}
        />;
    }

    return (
      <Host>
        <div class='nest-date-picker'>
          {this.label &&
            <div class='nest-date-picker__label'>{this.label}</div>
          }
          <div class="nest-date-picker__control">
            <input
              ref={inputEl => (this.nativeInput = inputEl)}
              onInput={this.onInput}
              value={value}
              placeholder={this.placeholder}
              onFocus={() => this.showCalendar = true}
              readonly={true}
              class="p-1 px-2 bg-background appearance-none outline-none w-full"
            />
            {(this.clearable && this.value) && (
              <div>
                <button
                  class="cursor-pointer w-6 h-full flex items-center outline-none focus:outline-none"
                  onClick={() => this.clearInput()}
                >
                  <nest-icon name='close-circle' class='w-4 h-4'></nest-icon>
                </button>
              </div>
            )}
            <div
              class="text-gray-300 w-8 py-1 pl-3 pr-1 border-l border-input flex items-center"
              onClick={this.toggle}
            >
              <button class="cursor-pointer w-6 h-6 text-gray-600 outline-none focus:outline-none transition-all">
                <nest-icon name='calendar'></nest-icon>
              </button>
            </div>
          </div>

          { this.showCalendar && (
          <div class="absolute top-100 z-50 w-80 flex flex-col border border-input shadow rounded overflow-hidden bg-background">
            <div class="p-3">
              <div class="space-y-0.5">{ contentView }</div>
            </div>
          </div>
          )}
        </div>
      </Host>
    );
  }

}
